import React, { useState, useEffect } from 'react';
import {
  History,
  Search,
  Filter,
  Download,
  Eye,
  RefreshCw,
  DollarSign,
  Calendar,
  CreditCard,
  FileText,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { getAllPaymentHistory, getPaymentHistory, generatePaymentReceipt } from '../services/paymentService';
import { companyDatabase } from '../utils/mainAppDatabase';
import { formatCurrency, formatDate } from '../utils/subscriptionCalculations';

const STALE_MS = 5 * 60 * 1000;

const PaymentHistory = () => {
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [lastSyncSource, setLastSyncSource] = useState('listener');
  const [isStale, setIsStale] = useState(false);

  const loadData = async (source = 'manual_refresh') => {
    try {
      setLoading(true);
      
      // Load companies
      const companiesData = await companyDatabase.getAllCompanies();
      setCompanies(companiesData);
      
      // Load all payments
      const paymentsData = await getAllPaymentHistory(200);
      setPayments(paymentsData);
      setLastSyncedAt(new Date().toISOString());
      setLastSyncSource(source);
      setIsStale(false);
      
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData('listener');
  }, []);

  useEffect(() => {
    applyFilters();
  }, [selectedCompany, selectedType, searchTerm, payments]);

  useEffect(() => {
    if (!lastSyncedAt) return undefined;
    const tick = () => {
      const age = Date.now() - new Date(lastSyncedAt).getTime();
      setIsStale(age > STALE_MS);
    };
    tick();
    const id = setInterval(tick, 30_000);
    return () => clearInterval(id);
  }, [lastSyncedAt]);

  const applyFilters = () => {
    let filtered = [...payments];

    // Filter by company
    if (selectedCompany !== 'all') {
      filtered = filtered.filter(p => p.company_id === selectedCompany);
    }

    // Filter by type
    if (selectedType !== 'all') {
      filtered = filtered.filter(p => p.type === selectedType);
    }

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(p =>
        p.reference?.toLowerCase().includes(search) ||
        p.notes?.toLowerCase().includes(search) ||
        p.company_id?.toLowerCase().includes(search) ||
        p.method?.toLowerCase().includes(search)
      );
    }

    setFilteredPayments(filtered);
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company?.name || companyId;
  };

  const getPaymentTypeLabel = (type) => {
    const labels = {
      plan: 'Plan Subscription',
      topup: 'Top-up Purchase',
      upgrade: 'Plan Upgrade'
    };
    return labels[type] || type;
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      manual: 'Manual',
      cash: 'Cash',
      bank_transfer: 'Bank Transfer',
      cheque: 'Cheque',
      upi: 'UPI',
      card: 'Card',
      stripe: 'Stripe',
      razorpay: 'Razorpay',
      other: 'Other'
    };
    return labels[method] || method;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Calendar },
      failed: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle }
    };

    const config = statusConfig[status] || statusConfig.completed;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${config.bg} ${config.text}`}>
        <Icon className="w-3 h-3" />
        {status || 'completed'}
      </span>
    );
  };

  const handleViewDetails = (payment) => {
    setSelectedPayment(payment);
    setShowDetailModal(true);
  };

  const handleDownloadReceipt = (payment) => {
    const receipt = generatePaymentReceipt(payment);
    
    // Create a simple text receipt
    const receiptText = `
PAYMENT RECEIPT
================

Receipt ID: ${receipt.receiptId}
Date: ${formatDate(receipt.date)}

Company ID: ${receipt.companyId}
Company Name: ${getCompanyName(receipt.companyId)}

Payment Details:
- Type: ${getPaymentTypeLabel(receipt.type)}
- Amount: ${formatCurrency(receipt.amount, '₹')}
- Method: ${getPaymentMethodLabel(receipt.method)}
- Reference: ${receipt.reference || 'N/A'}
- Status: ${receipt.status}

Generated: ${formatDate(receipt.generatedAt)}

---
AccToo Backoffice
    `.trim();

    // Download as text file
    const blob = new Blob([receiptText], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `receipt-${receipt.receiptId}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const totalAmount = filteredPayments.reduce((sum, p) => sum + (p.amount || 0), 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <History className="w-8 h-8 text-blue-600" />
                <h1 className="text-3xl font-bold text-gray-800">Payment History</h1>
              </div>
              <p className="text-gray-600">View and manage all payment records</p>
              {lastSyncedAt && (
                <p className={`text-xs mt-1 ${isStale ? 'text-amber-700' : 'text-gray-500'}`}>
                  Last synced: {lastSyncedAt} · source: {lastSyncSource}
                  {isStale ? ' · data may be stale — refresh before support actions' : ''}
                </p>
              )}
            </div>
            <button
              onClick={() => loadData('manual_refresh')}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all disabled:opacity-50"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <DollarSign className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <div className="text-sm text-gray-600">Total Payments</div>
                <div className="text-2xl font-bold text-gray-800">{filteredPayments.length}</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <div className="text-sm text-gray-600">Total Amount</div>
                <div className="text-2xl font-bold text-gray-800">₹{totalAmount.toFixed(2)}</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-100 rounded-lg">
                <FileText className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <div className="text-sm text-gray-600">Plan Payments</div>
                <div className="text-2xl font-bold text-gray-800">
                  {filteredPayments.filter(p => p.type === 'plan').length}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-orange-100 rounded-lg">
                <CreditCard className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <div className="text-sm text-gray-600">Top-up Payments</div>
                <div className="text-2xl font-bold text-gray-800">
                  {filteredPayments.filter(p => p.type === 'topup').length}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search payments..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Company Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
              >
                <option value="all">All Companies</option>
                {companies.map(company => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Type Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
              >
                <option value="all">All Types</option>
                <option value="plan">Plan Subscription</option>
                <option value="topup">Top-up Purchase</option>
                <option value="upgrade">Plan Upgrade</option>
              </select>
            </div>
          </div>
        </div>

        {/* Payments Table */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Method
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredPayments.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                      {loading ? 'Loading payments...' : 'No payments found'}
                    </td>
                  </tr>
                ) : (
                  filteredPayments.map((payment) => (
                    <tr key={payment.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(payment.date || payment.recorded_at?.toDate?.())}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {getCompanyName(payment.company_id)}
                        </div>
                        <div className="text-xs text-gray-500">{payment.company_id}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {getPaymentTypeLabel(payment.type)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-bold text-gray-900">
                          ₹{payment.amount?.toFixed(2)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="px-2 py-1 bg-gray-100 rounded text-xs">
                          {getPaymentMethodLabel(payment.method)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(payment.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleViewDetails(payment)}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                          title="View Details"
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDownloadReceipt(payment)}
                          className="text-green-600 hover:text-green-900"
                          title="Download Receipt"
                        >
                          <Download className="w-5 h-5" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Detail Modal */}
        {showDetailModal && selectedPayment && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-800">Payment Details</h3>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Payment ID</div>
                    <div className="font-medium text-gray-900">{selectedPayment.id}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Date</div>
                    <div className="font-medium text-gray-900">
                      {formatDate(selectedPayment.date || selectedPayment.recorded_at?.toDate?.())}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Company</div>
                    <div className="font-medium text-gray-900">{getCompanyName(selectedPayment.company_id)}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Type</div>
                    <div className="font-medium text-gray-900">{getPaymentTypeLabel(selectedPayment.type)}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Amount</div>
                    <div className="text-2xl font-bold text-blue-700">₹{selectedPayment.amount?.toFixed(2)}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Method</div>
                    <div className="font-medium text-gray-900">{getPaymentMethodLabel(selectedPayment.method)}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Reference</div>
                    <div className="font-medium text-gray-900">{selectedPayment.reference || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Status</div>
                    <div>{getStatusBadge(selectedPayment.status)}</div>
                  </div>
                </div>

                {selectedPayment.notes && (
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Notes</div>
                    <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-900">
                      {selectedPayment.notes}
                    </div>
                  </div>
                )}

                {selectedPayment.recorded_by && (
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Recorded By</div>
                    <div className="text-sm text-gray-900">
                      {selectedPayment.recorded_by.adminName || selectedPayment.recorded_by.adminEmail}
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all font-medium"
                >
                  Close
                </button>
                <button
                  onClick={() => handleDownloadReceipt(selectedPayment)}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all font-medium flex items-center justify-center gap-2"
                >
                  <Download className="w-5 h-5" />
                  Download Receipt
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentHistory;



















































